/**
 * Background Fetch Utility
 * Ensures API calls complete even when the browser tab is switched
 * or the user is on mobile and switches apps
 */

import axios from "axios";

/**
 * Stores pending API requests in IndexedDB for persistence
 * This ensures requests aren't lost if the page is closed
 */
class RequestQueue {
  constructor() {
    this.dbName = "shamudeen_requests";
    this.storeName = "pending_requests";
    this.db = null;
  }

  /**
   * Initialize IndexedDB
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, {
            keyPath: "id",
            autoIncrement: true,
          });
        }
      };
    });
  }

  /**
   * Add a request to the queue
   */
  async addRequest(request) {
    if (!this.db) {
      await this.initDB();
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(this.storeName, "readwrite");
      const store = tx.objectStore(this.storeName);
      const addRequest = store.add(request);

      addRequest.onerror = () => reject(addRequest.error);
      addRequest.onsuccess = () => resolve(addRequest.result);
    });
  }

  /**
   * Update an existing request in the queue (uses store.put to avoid ConstraintError)
   */
  async updateRequest(request) {
    if (!this.db) {
      await this.initDB();
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(this.storeName, "readwrite");
      const store = tx.objectStore(this.storeName);
      const putRequest = store.put(request);

      putRequest.onerror = () => reject(putRequest.error);
      putRequest.onsuccess = () => resolve(putRequest.result);
    });
  }

  /**
   * Get all pending requests
   */
  async getAllRequests() {
    if (!this.db) {
      await this.initDB();
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(this.storeName, "readonly");
      const store = tx.objectStore(this.storeName);
      const getRequest = store.getAll();

      getRequest.onerror = () => reject(getRequest.error);
      getRequest.onsuccess = () => resolve(getRequest.result);
    });
  }

  /**
   * Remove a request from the queue
   */
  async removeRequest(id) {
    if (!this.db) {
      await this.initDB();
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(this.storeName, "readwrite");
      const store = tx.objectStore(this.storeName);
      const deleteRequest = store.delete(id);

      deleteRequest.onerror = () => reject(deleteRequest.error);
      deleteRequest.onsuccess = () => resolve();
    });
  }
}

const requestQueue = new RequestQueue();
let listenersInitialized = false;

/**
 * Make a fetch request that continues in the background
 * @param {string} url - API endpoint
 * @param {object} config - Axios config (includes method, data, etc.)
 * @param {object} callbacks - Callback functions { onSuccess, onError, onBackground, persistRetry }
 * @returns {Promise}
 */
export async function backgroundFetch(url, config = {}, callbacks = {}) {
  const {
    onSuccess,
    onError,
    onBackground,
    persistRetry = config?.data?.priority !== "prefetch",
  } = callbacks;

  let requestId = null;
  try {
    if (persistRetry && typeof indexedDB !== "undefined") {
      const requestData = {
        url,
        config,
        timestamp: new Date().toISOString(),
        retries: 0,
        maxRetries: 3,
      };
      requestId = await requestQueue.addRequest(requestData);
    }

    // Attempt the fetch
    const response = await axios(url, config);

    // Success - remove from queue and trigger callback
    if (requestId !== null) {
      await requestQueue.removeRequest(requestId);
    }

    if (onSuccess) {
      onSuccess(response.data);
    }

    return response;
  } catch (error) {
    // Error occurred
    if (onError) {
      onError(error);
    }

    // Notify that request is queued for background retry
    if (persistRetry && onBackground) {
      onBackground({
        message: "Request queued for background processing",
        error: error.message,
      });
    }

    // Request will be retried when connection is restored
    throw error;
  }
}

/**
 * Retry all pending requests in the queue
 * Call this when the page/app becomes active or connection is restored
 */
export async function retryPendingRequests() {
  if (typeof indexedDB === "undefined") {
    return { retried: 0, successful: 0, failed: 0 };
  }
  const requests = await requestQueue.getAllRequests();

  if (requests.length === 0) {
    return { retried: 0, successful: 0, failed: 0 };
  }

  const results = {
    retried: 0,
    successful: 0,
    failed: 0,
  };

  const now = Date.now();
  const MAX_REQUEST_AGE_MS = 15 * 60 * 1000; // 15 minutes

  for (const request of requests) {
    const age = request.timestamp ? now - new Date(request.timestamp).getTime() : Infinity;
    if (age > MAX_REQUEST_AGE_MS || request.config?.data?.priority === 'prefetch') {
      await requestQueue.removeRequest(request.id);
      continue;
    }
    results.retried++;
    try {
      const response = await axios(request.url, request.config);

      // Remove successful request
      await requestQueue.removeRequest(request.id);
      results.successful++;

      // Trigger a custom event that components can listen to
      window.dispatchEvent(
        new CustomEvent("backgroundRequestSuccess", {
          detail: {
            url: request.url,
            chapterUrl: request.config?.data?.url || null,
            response: response.data,
          },
        })
      );
    } catch (error) {
      // Increment retry count
      request.retries = (request.retries || 0) + 1;

      if (request.retries >= request.maxRetries) {
        // Give up after max retries
        await requestQueue.removeRequest(request.id);
        results.failed++;

        window.dispatchEvent(
          new CustomEvent("backgroundRequestFailed", {
            detail: { url: request.url, error: error.message },
          })
        );
      } else {
        // Update retry count in DB using put (fixes F7 ConstraintError)
        await requestQueue.updateRequest(request);
      }
    }
  }

  return results;
}

/**
 * Set up listeners for page visibility and app lifecycle events (run-once guard)
 */
export function setupBackgroundListeners() {
  if (listenersInitialized || typeof window === "undefined") {
    return;
  }
  listenersInitialized = true;

  // Handle page visibility changes
  document.addEventListener("visibilitychange", async () => {
    if (document.visibilityState === "visible") {
      // Page became visible - retry pending requests
      const results = await retryPendingRequests();
      if (results.successful > 0) {
        console.log(
          `✅ Successfully retried ${results.successful} background requests`
        );
      }
    }
  });

  // Handle online/offline events
  window.addEventListener("online", async () => {
    console.log("🌐 Connection restored - retrying pending requests");
    const results = await retryPendingRequests();
    if (results.successful > 0) {
      console.log(
        `✅ Successfully retried ${results.successful} requests after reconnection`
      );
    }
  });

  window.addEventListener("offline", () => {
    console.log("📡 Connection lost - requests will be retried when online");
  });

  // Mobile app lifecycle (if using a framework like Cordova/Capacitor)
  if (window.document && window.document.addEventListener) {
    document.addEventListener("resume", async () => {
      console.log("📱 App resumed - retrying pending requests");
      const results = await retryPendingRequests();
      if (results.successful > 0) {
        console.log(
          `✅ Successfully retried ${results.successful} requests after app resume`
        );
      }
    });
  }
}

/**
 * Get status of pending requests
 */
export async function getPendingRequestsStatus() {
  const requests = await requestQueue.getAllRequests();
  return {
    count: requests.length,
    requests: requests,
  };
}
